import { Component, computed, OnInit, Signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { NaturalStoneService } from '../../../services/natural-stone.service'
import { NaturalStoneLot, NaturalStoneLots } from '../../../interfaces/natural-stone/natural-stone-lots'
import { DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { environment } from '../../../../environments/environment'
import { ImageViewerComponent } from '@components/image-viewer/image-viewer.component'
import { NumberStepperComponent } from '@components/number-stepper/number-stepper.component'
import { ButtonComponent } from '@components/button/button.component'

@Component({
  selector: 'app-natural-stone-lot-detail',
  standalone: true,
    imports: [
        NgForOf,
        NgIf,
        TranslateModule,
        DecimalPipe,
        ImageViewerComponent,
        NgClass,
        NumberStepperComponent,
        ButtonComponent
    ],
  templateUrl: './natural-stone-lot-detail.component.html',
  styleUrl: './natural-stone-lot-detail.component.scss'
})
export class NaturalStoneLotDetailComponent implements OnInit {
    centerId: string;
    sku: string;
    lotId: string;
    bundleId?: string;

    lots: Signal<NaturalStoneLot[]>;
    lotIndex: number = 0;
    colorInfo: Signal<NaturalStoneLots>
    isUsa: boolean;

    brandText?: string;
    colorText?: string;
    finishText?: string;

    imageUrl: SafeUrl;
    zoom: {level: number} = {level: 1};

    qtyToAddToCart: number = 1

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private naturalStoneService: NaturalStoneService,
        private translate: TranslateService,
        private sanitizer: DomSanitizer
    ) {
    }

    ngOnInit() {
        this.lots = computed(() => {
            const color = this.naturalStoneService.selectedColor()
            if (!color.lots) return []

            const output = this.findLotForLotId(color)

            if (output.length > 0 && output[0].bundleId) {
                this.bundleId = output[0].bundleId
                output.push(...this.findLotsForBundleId(color, output[0].lotId!))
            }

            return output;
        })

        this.colorInfo = computed(() => {
            const color = this.naturalStoneService.selectedColor()
            this.brandText = this.naturalStoneService.getBrandText(color)
            this.colorText = this.naturalStoneService.getColorText(color)
            this.getFinish(color)

            return color
        })

        this.route.params.subscribe(params => {
            this.centerId = params['centerId']
            this.sku = params['sku']
            this.lotId = params['lotId']

            if (!this.naturalStoneService.selectedColor().lots) {
                this.naturalStoneService.loadColor(this.centerId, this.sku)
            }
            this.isUsa = this.naturalStoneService.isUsa
        })
    }

    changeLot(newLotIndex: number){
        if(newLotIndex >= this.lots().length){
            newLotIndex = 0
        } else if(newLotIndex < 0){
            newLotIndex = this.lots().length - 1
        }

        this.lotIndex = newLotIndex
        this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(environment.lotImageCdn.replace('{lotId}', this.getCurrentLot().lotId!)) //NOSONAR
    }

    findLotForLotId(colorLots: NaturalStoneLots) {
        const lot = colorLots.lots!.find(lot => lot.lotId === this.lotId)
        if (lot){
            this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(environment.lotImageCdn.replace('{lotId}', lot.lotId!)) //NOSONAR
            return [lot]
        }
        return []
    }

    findLotsForBundleId(colorLots: NaturalStoneLots, lotIdToExclude: string) {
        return colorLots.lots!.filter(lot => lot.bundleId === this.bundleId && lot.lotId !== lotIdToExclude)
    }

    goBack() {
        this.router.navigate(['natural-stone', 'lots', this.centerId, this.sku])
    }

    getCurrentLot(){
        return this.lots()[this.lotIndex]
    }

    getThickness() {
        return parseFloat(this.colorInfo().thickness!)
    }

    getFinish(color: NaturalStoneLots) {
        this.translate.get('finishes.' + color.finish!).subscribe({
            next: (result: string) => {
                if(result && !result.includes('finishes.')){
                    this.finishText = result
                } else {
                    this.finishText = this.colorInfo().finish!
                }
            }
        })
    }

    getWidth() {
        return this.isUsa ? this.getCurrentLot().widthUsa : this.getCurrentLot().widthEur
    }

    getHeight() {
        return this.isUsa ? this.getCurrentLot().heightUsa : this.getCurrentLot().heightEur
    }

    getMeasureUnit() {
        return this.isUsa ? ' ft' : ' cm'
    }

    getSurfaceArea() {
        return this.isUsa ? this.getCurrentLot().surfaceAreaUsa : this.getCurrentLot().surfaceAreaEur
    }

    getSurfaceAreaMeasure() {
        return this.isUsa ? ' SqFt' : ' m²'
    }

    getAvailableSlabs(): number {
        return this.naturalStoneService.getAvailableSlabsForLotAndBundle(this.getCurrentLot().lotId!, this.getCurrentLot().bundleId)
    }

    downloadImage() {
        const a = document.createElement('a')
        a.href = environment.lotImageCdn.replace('{lotId}', this.getCurrentLot().lotId!)
        a.target = '_blank'
        document.body.appendChild(a)

        a.click()
    }

    zoomIn(){
        this.zoom = {level: 1}
    }

    zoomOut(){
        this.zoom = {level: -1}
    }

    qtyToAddToCartChanged(qtyToAddToCart: number) {
        this.qtyToAddToCart = qtyToAddToCart
    }

    addToCart(){
        this.naturalStoneService.isMinicartOpen.set(true)
        this.naturalStoneService.addLotToCart(this.getCurrentLot(), this.qtyToAddToCart)
        this.goBack()
    }
}
